import { useState } from 'react';
import { Star, Send, Loader2, Package } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface Product {
  id: string;
  name: string;
  image_url: string | null;
}

interface QuickReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: Product[];
  orderId?: string;
  onReviewSubmitted?: () => void;
}

const StarRating = ({ 
  rating, 
  onRate, 
  size = 'md' 
}: { 
  rating: number; 
  onRate: (rating: number) => void;
  size?: 'sm' | 'md' | 'lg';
}) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className="focus:outline-none transition-transform hover:scale-110"
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          onClick={() => onRate(star)}
        >
          <Star
            className={`${sizeClasses[size]} ${
              (hoverRating || rating) >= star
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-muted-foreground'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

interface ReviewState {
  rating: number;
  comment: string;
  submitted: boolean;
  submitting: boolean;
}

export const QuickReviewModal = ({ 
  open, 
  onOpenChange, 
  products, 
  orderId,
  onReviewSubmitted 
}: QuickReviewModalProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [reviews, setReviews] = useState<Record<string, ReviewState>>(() => {
    const initial: Record<string, ReviewState> = {};
    products.forEach(p => {
      initial[p.id] = { rating: 0, comment: '', submitted: false, submitting: false };
    });
    return initial;
  });

  const handleSubmitReview = async (productId: string) => {
    if (!user) return;
    
    const review = reviews[productId];
    if (review.rating === 0) {
      toast({
        title: 'Выберите оценку',
        description: 'Нажмите на звёзды, чтобы оценить товар',
        variant: 'destructive',
      });
      return;
    }

    setReviews(prev => ({
      ...prev,
      [productId]: { ...prev[productId], submitting: true }
    }));

    try {
      const { error } = await supabase
        .from('reviews')
        .insert({
          product_id: productId,
          user_id: user.id,
          rating: review.rating,
          comment: review.comment.trim() || null,
        });

      if (error) throw error;

      setReviews(prev => ({
        ...prev,
        [productId]: { ...prev[productId], submitted: true, submitting: false }
      }));

      toast({ title: 'Спасибо за отзыв!' });
      onReviewSubmitted?.();
    } catch (error: any) {
      console.error('Error submitting review:', error);
      toast({
        title: 'Ошибка',
        description: error.message?.includes('duplicate') 
          ? 'Вы уже оставили отзыв на этот товар' 
          : 'Не удалось отправить отзыв',
        variant: 'destructive',
      });
      setReviews(prev => ({
        ...prev,
        [productId]: { ...prev[productId], submitting: false }
      }));
    }
  };

  const allSubmitted = products.every(p => reviews[p.id]?.submitted);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-400" />
            Оцените товары
          </DialogTitle>
          <DialogDescription>
            {orderId 
              ? `Поделитесь впечатлениями о товарах из заказа #${orderId.substring(0, 8)}`
              : 'Поделитесь впечатлениями о приобретённых товарах'
            }
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-4 pr-4">
            {products.map((product, index) => {
              const review = reviews[product.id] || { rating: 0, comment: '', submitted: false, submitting: false };
              
              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 rounded-lg border ${
                    review.submitted 
                      ? 'bg-eco/10 border-eco/30' 
                      : 'bg-muted/50 border-border'
                  }`}
                >
                  <div className="flex gap-3 mb-3">
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-muted shrink-0">
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-6 w-6 text-muted-foreground/50" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{product.name}</p>
                      {review.submitted ? (
                        <p className="text-xs text-eco mt-1">✓ Отзыв отправлен</p>
                      ) : (
                        <div className="mt-2">
                          <StarRating 
                            rating={review.rating} 
                            onRate={(rating) => setReviews(prev => ({
                              ...prev,
                              [product.id]: { ...prev[product.id], rating }
                            }))}
                            size="md"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {!review.submitted && (
                    <div className="space-y-3">
                      <Textarea
                        placeholder="Комментарий (необязательно)..."
                        value={review.comment}
                        onChange={(e) => setReviews(prev => ({
                          ...prev,
                          [product.id]: { ...prev[product.id], comment: e.target.value }
                        }))}
                        className="resize-none text-sm"
                        rows={2}
                      />
                      <Button
                        size="sm"
                        onClick={() => handleSubmitReview(product.id)}
                        disabled={review.submitting || review.rating === 0}
                        className="w-full gap-2"
                      >
                        {review.submitting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        Отправить
                      </Button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </ScrollArea>

        {allSubmitted && (
          <div className="pt-4 border-t">
            <Button 
              className="w-full" 
              onClick={() => onOpenChange(false)}
            >
              Готово
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
